import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type {
  RealtimeConfig,
  RealtimeConnectionState,
  RealtimeContextState,
  RealtimeFeatureFlags,
} from '@klareospace/pharos-lib-types';
import { useApiClient } from '../api/api-client-context';
import { WebPubSubService } from './web-pubsub-service';

interface RealtimeProviderProps {
  /** Name of the API client instance to use for negotiate calls (e.g., 'admin-auth', 'deliveries') */
  apiClientName: string;
  /** Whether to auto-connect when the provider mounts (default: true when enabled) */
  autoConnect?: boolean;
  children: React.ReactNode;
  /** Realtime configuration. If not provided, realtime is disabled. */
  config?: RealtimeConfig;
  /** Master enable/disable flag (default: false — opt-in) */
  enabled?: boolean;
  /** Per-resource feature flags */
  featureFlags?: RealtimeFeatureFlags;
  /** Tenant ID for multi-tenant isolation */
  tenantId?: string;
  /** User ID for the connection */
  userId?: string;
}

type RealtimeContextValue = RealtimeContextState & {
  featureFlags: RealtimeFeatureFlags;
  service: WebPubSubService;
};

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

export function RealtimeProvider({
  apiClientName,
  autoConnect = true,
  children,
  config,
  enabled = false,
  featureFlags = {},
  tenantId,
  userId,
}: RealtimeProviderProps): React.JSX.Element {
  const apiClient = useApiClient(apiClientName);
  const serviceRef = useRef<WebPubSubService>(new WebPubSubService());
  const [connectionState, setConnectionState] = useState<RealtimeConnectionState>('disconnected');
  const [connectionError, setConnectionError] = useState<Error | null>(null);

  // Subscribe to connection state changes from the service.
  useEffect(() => {
    const service = serviceRef.current;
    const unsubscribe = service.onConnectionStateChanged((state, error) => {
      setConnectionState(state);
      setConnectionError(error ?? null);
    });

    return unsubscribe;
  }, []);

  // Tear down the connection when auth context is lost (enabled=false, tenantId/userId cleared).
  // disconnect() resets state to 'disconnected', re-arming the connect effect below so that
  // a fresh negotiate runs automatically when auth context is restored.
  useEffect(() => {
    const service = serviceRef.current;
    const canConnect = enabled && !!config && !!tenantId && !!userId;
    if (!canConnect && service.getState() !== 'disconnected') {
      service.disconnect();
    }
  }, [enabled, config, tenantId, userId]);

  // Auto-connect when all prerequisites are met and the service is disconnected.
  useEffect(() => {
    const service = serviceRef.current;

    const shouldConnect =
      enabled && autoConnect && !!config && !!tenantId && !!userId && connectionState === 'disconnected';

    if (!shouldConnect) return;

    service.connect(config, apiClient.instance, tenantId, userId).catch((error: unknown) => {
      console.error('[RealtimeProvider] Connection error:', error);
    });
  }, [enabled, autoConnect, config, tenantId, userId, apiClient, connectionState]);

  // Disconnect on unmount.
  useEffect(() => {
    const service = serviceRef.current;
    return (): void => {
      service.disconnect();
    };
  }, []);

  const connect = useCallback(async (): Promise<void> => {
    if (!config || !tenantId || !userId) return;
    await serviceRef.current.connect(config, apiClient.instance, tenantId, userId);
  }, [config, tenantId, userId, apiClient]);

  const disconnect = useCallback((): void => {
    serviceRef.current.disconnect();
  }, []);

  const isConnected = connectionState === 'connected';

  const value = useMemo<RealtimeContextValue>(
    () => ({
      connect,
      disconnect,
      error: connectionError,
      featureFlags,
      isConnected,
      isEnabled: enabled,
      service: serviceRef.current,
      state: connectionState,
    }),
    [connect, disconnect, connectionError, featureFlags, isConnected, enabled, connectionState],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

/**
 * Hook to access the realtime context.
 * Returns a safe no-op object if used outside RealtimeProvider (never errors).
 */
export function useRealtimeContext(): RealtimeContextState & { featureFlags: RealtimeFeatureFlags } {
  const context = useContext(RealtimeContext);

  if (!context) {
    return {
      connect: async (): Promise<void> => {},
      disconnect: (): void => {},
      error: null,
      featureFlags: {},
      isConnected: false,
      isEnabled: false,
      state: 'disconnected' as const,
    };
  }

  return context;
}

/**
 * Internal hook that returns the full context including the service reference.
 * Exported from this module for sibling hooks (e.g. `use-realtime.ts`) only — not re-exported
 * from the package's public barrel. Use `useRealtimeContext()` for the public API.
 */
export function useInternalRealtimeContext(): RealtimeContextValue | null {
  return useContext(RealtimeContext);
}
